// AI Chat Service for ChatGPT API configuration
const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const API_URL = 'https://api.openai.com/v1/chat/completions';
const BACKEND_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

class AIChatService {
  constructor() {
    this.systemPrompt = `You are an AI assistant for QQINV, an inventory management system. You help users with:

1. Inventory Management: Stock levels, product information, low stock alerts
2. Sales & Orders: Order processing, customer management, sales analytics
3. Purchasing: Purchase orders, vendor management, receiving
4. Categories: Product categorization and organization
5. Locations: Warehouse and storage management
6. Reports: Analytics and business insights

Key features of the system:
- Multi-tenant support with tenant isolation
- Real-time inventory tracking
- Barcode scanning capabilities
- E-way bill management
- Quote and order management
- Comprehensive reporting

Provide helpful, concise responses. When users ask about specific data, remind them that you can help navigate to relevant pages or explain features. Keep responses under 150 words and be conversational.`;
  }

  async fetchStats() {
    try {
      console.log('🤖 Fetching stats from:', `${BACKEND_API_URL}/ai/stats`);
      const response = await fetch(`${BACKEND_API_URL}/ai/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('🤖 Stats response status:', response.status);
      
      if (!response.ok) {
        console.error('🤖 Stats API error:', response.status, response.statusText);
        throw new Error(`Stats API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('🤖 Stats data received:', result);
      return result.data;
    } catch (error) {
      console.error('🤖 Failed to fetch stats:', error);
      return null;
    }
  }

  async sendMessage(messages) {
    const userMessage = messages[messages.length - 1].content.toLowerCase();
    console.log('🤖 User message:', userMessage);
    
    // Check if user is asking for stats/data
    const isStatsQuery = userMessage.includes('stats') || 
                        userMessage.includes('statistics') || 
                        userMessage.includes('data') ||
                        userMessage.includes('metrics') ||
                        userMessage.includes('numbers') ||
                        userMessage.includes('how many') ||
                        userMessage.includes('low stock') ||
                        userMessage.includes('out of stock') ||
                        userMessage.includes('inventory') ||
                        userMessage.includes('products') ||
                        userMessage.includes('dashboard') ||
                        userMessage.includes('overview');

    console.log('🤖 Is stats query:', isStatsQuery);

    let statsData = null;
    if (isStatsQuery) {
      console.log('🤖 Fetching stats data...');
      statsData = await this.fetchStats();
      console.log('🤖 Stats data result:', statsData);
    }

    // Check if API key is configured
    if (!API_KEY || API_KEY === 'your_openai_api_key_here') {
      return this.getMockResponse(messages[messages.length - 1].content, statsData);
    }

    try {
      // Enhance system prompt with real data if available
      let enhancedSystemPrompt = this.systemPrompt;
      if (statsData) {
        enhancedSystemPrompt += `\n\nCurrent Real-Time Inventory Data:
- Total Products: ${statsData.inventory.totalProducts}
- Total Categories: ${statsData.inventory.totalCategories}
- Total Stock Units: ${statsData.inventory.totalStock}
- Low Stock Items: ${statsData.inventory.lowStockCount}
- Out of Stock Items: ${statsData.inventory.outOfStockCount}
- Total Alerts: ${statsData.alerts.totalAlerts}

${statsData.inventory.lowStockItems.length > 0 ? `Low Stock Items:
${statsData.inventory.lowStockItems.map(item => `- ${item.name} (${item.sku}): ${item.current}/${item.reorderPoint}`).join('\n')}` : ''}

${statsData.inventory.outOfStockItems.length > 0 ? `Out of Stock Items:
${statsData.inventory.outOfStockItems.map(item => `- ${item.name} (${item.sku})`).join('\n')}` : ''}

Use this real data to provide accurate, specific answers about the current inventory status.`;
      }

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            { role: 'system', content: enhancedSystemPrompt },
            ...messages.map(msg => ({
              role: msg.role === 'ai' ? 'assistant' : 'user',
              content: msg.content
            }))
          ],
          max_tokens: 300,
          temperature: 0.7,
        }),
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('AI Chat Service Error:', error);
      // Fallback to mock response
      return this.getMockResponse(messages[messages.length - 1].content);
    }
  }

  getMockResponse(userMessage, statsData = null) {
    const text = userMessage.toLowerCase();
    
    // Stats/data related responses with real data
    if ((text.includes('stats') || text.includes('statistics') || text.includes('data') || 
         text.includes('metrics') || text.includes('numbers') || text.includes('overview') ||
         text.includes('dashboard')) && statsData) {
      return `📊 Here are your current inventory statistics:

**Inventory Overview:**
• Total Products: ${statsData.inventory.totalProducts}
• Total Categories: ${statsData.inventory.totalCategories}
• Total Stock Units: ${statsData.inventory.totalStock}

**Alerts & Attention Needed:**
• Low Stock Items: ${statsData.inventory.lowStockCount}
• Out of Stock Items: ${statsData.inventory.outOfStockCount}
• Total Alerts: ${statsData.alerts.totalAlerts}

${statsData.inventory.lowStockItems.length > 0 ? `\n**Low Stock Items:**\n${statsData.inventory.lowStockItems.slice(0, 3).map(item => `• ${item.name} (${item.sku}): ${item.current}/${item.reorderPoint} units`).join('\n')}${statsData.inventory.lowStockItems.length > 3 ? '\n• ...and more' : ''}` : ''}

${statsData.inventory.outOfStockItems.length > 0 ? `\n**Out of Stock:**\n${statsData.inventory.outOfStockItems.slice(0, 3).map(item => `• ${item.name} (${item.sku})`).join('\n')}${statsData.inventory.outOfStockItems.length > 3 ? '\n• ...and more' : ''}` : ''}

Would you like me to help you navigate to specific areas that need attention?`;
    }
    
    // Inventory related responses
    if (text.includes('inventory') || text.includes('stock')) {
      if (statsData) {
        return `📦 Your inventory status: ${statsData.inventory.totalProducts} products with ${statsData.inventory.totalStock} total units. ${statsData.alerts.totalAlerts > 0 ? `⚠️ ${statsData.alerts.totalAlerts} items need attention (${statsData.inventory.lowStockCount} low stock, ${statsData.inventory.outOfStockCount} out of stock).` : '✅ All stock levels look good!'} Need help with specific products?`;
      }
      return "I can help you with inventory management! Your current stock levels look healthy overall. Would you like me to show you low-stock items or help you navigate to the Products page for detailed inventory management?";
    }
    
    // Sales related responses
    if (text.includes('sales') || text.includes('order')) {
      return "Sales are performing well! I can help you track orders, manage customer relationships, or analyze sales trends. Would you like to check recent orders or view sales analytics?";
    }
    
    // Purchase related responses
    if (text.includes('purchase') || text.includes('vendor') || text.includes('po')) {
      return "For purchasing, I can help you manage purchase orders, track vendor relationships, and monitor receiving. Would you like to check pending POs or review vendor performance?";
    }
    
    // Categories related responses
    if (text.includes('categor')) {
      return "Categories help organize your products efficiently. I can guide you through creating hierarchical categories or help you reorganize existing product classifications. Need help with category management?";
    }
    
    // Locations related responses
    if (text.includes('location') || text.includes('warehouse')) {
      return "Location management is key for inventory tracking! I can help you manage warehouse locations, track stock by location, or set up new storage areas. What location-related task can I assist with?";
    }
    
    // Reports related responses
    if (text.includes('report') || text.includes('analytic')) {
      return "I can help you understand your business analytics! From inventory reports to sales trends, I'll guide you to the right insights. What specific metrics or reports are you interested in?";
    }
    
    // General navigation help
    if (text.includes('help') || text.includes('how')) {
      return "I'm here to help! I can assist with inventory management, sales tracking, purchasing, and more. Try asking about specific features like 'show me low stock items' or 'help with purchase orders'. What would you like to explore?";
    }
    
    // Default response
    return "I'm your QQINV assistant! I can help with inventory, sales, purchasing, categories, locations, and reports. Ask me about specific features or let me know what you'd like to accomplish today.";
  }

  // Get context-aware suggestions based on current page
  getPageContextSuggestions(currentPage) {
    const suggestions = {
      dashboard: [
        "Show me today's key metrics",
        "What needs my attention?",
        "Summarize inventory status"
      ],
      products: [
        "Show low stock items",
        "Help me add a new product",
        "Explain product categories"
      ],
      categories: [
        "How do I create subcategories?",
        "Help organize my products",
        "Show category hierarchy"
      ],
      orders: [
        "Show pending orders",
        "Help with order processing",
        "Track recent sales"
      ],
      locations: [
        "Manage warehouse locations",
        "Set up new storage area",
        "Track stock by location"
      ],
      reports: [
        "Generate inventory report",
        "Show sales analytics",
        "Explain key metrics"
      ]
    };

    return suggestions[currentPage] || [
      "How can I help you today?",
      "Ask about inventory features",
      "Get help with navigation"
    ];
  }
}

export default new AIChatService();
